import { getSupabaseBrowserClient } from '../lib/supabase'
import type { OrganisationRoleCode } from '../lib/governance-api'

export type CustomerServiceState = 'provisioning' | 'trial' | 'active' | 'suspended'
export type CustomerModuleCode = 'discovery' | 'strategy_roadmap' | 'ai_register_product_library'

export type OrganisationAccessContext = {
  membershipId: string
  status: string
  roles: OrganisationRoleCode[]
  organisation: {
    id: string
    name: string
    sector: string | null
    sizeBand: string | null
    countryCode: string
    onboardingStatus: string
  }
  service: {
    status: CustomerServiceState
    permitted: boolean
    customerMessage: string | null
    serviceCommencementAt: string | null
    trialEndsAt: string | null
    serviceEndsAt: string | null
    versionNo: number
  }
  entitledModules: CustomerModuleCode[]
}

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function stringValue(value: unknown, fallback = '') {
  return typeof value === 'string' ? value : fallback
}

function numberValue(value: unknown) {
  return typeof value === 'number' ? value : typeof value === 'string' ? Number(value) || 0 : 0
}

export async function loadOrganisationAccessContexts(): Promise<OrganisationAccessContext[]> {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc('list_my_organisation_access_contexts')

  if (error) throw new Error(error.message)
  if (!Array.isArray(data)) return []

  return data.map((value) => {
    if (!isObject(value) || !isObject(value.organisation) || !isObject(value.service)) {
      throw new Error('Organisation access service returned an invalid response.')
    }

    const organisation = value.organisation
    const service = value.service

    return {
      membershipId: stringValue(value.membershipId),
      status: stringValue(value.membershipStatus),
      roles: (Array.isArray(value.roles) ? value.roles : []) as OrganisationRoleCode[],
      organisation: {
        id: stringValue(organisation.id),
        name: stringValue(organisation.name),
        sector: typeof organisation.sector === 'string' ? organisation.sector : null,
        sizeBand: typeof organisation.sizeBand === 'string' ? organisation.sizeBand : null,
        countryCode: stringValue(organisation.countryCode, 'AU'),
        onboardingStatus: stringValue(organisation.onboardingStatus),
      },
      service: {
        status: stringValue(service.status, 'provisioning') as CustomerServiceState,
        permitted: service.permitted === true,
        customerMessage: typeof service.customerMessage === 'string' ? service.customerMessage : null,
        serviceCommencementAt: typeof service.serviceCommencementAt === 'string' ? service.serviceCommencementAt : null,
        trialEndsAt: typeof service.trialEndsAt === 'string' ? service.trialEndsAt : null,
        serviceEndsAt: typeof service.serviceEndsAt === 'string' ? service.serviceEndsAt : null,
        versionNo: numberValue(service.versionNo),
      },
      entitledModules: (Array.isArray(value.entitledModules) ? value.entitledModules : []) as CustomerModuleCode[],
    }
  })
}
